/**
 * Experience Repository Implementation
 *
 * Single Responsibility: Handles all experience-related data operations
 * Dependency Inversion: Depends on IHttpClient abstraction
 * Open/Closed: Can be extended without modifying existing methods
 */

import { IHttpClient } from '../interfaces/IHttpClient';
import { IExperienceRepository } from '../interfaces/IExperienceRepository';
import { ExperienceTransformer } from '../transformers/ExperienceTransformer';
import {
  Experience,
  ExperienceRequest,
  ExperienceListResponse,
  ExperienceStats,
} from '@/services/experienceService';
import { AxiosError } from 'axios';

export class ExperienceRepository implements IExperienceRepository {
  private httpClient: IHttpClient;
  private baseUrl: string;

  constructor(httpClient: IHttpClient, baseUrl: string) {
    this.httpClient = httpClient;
    this.baseUrl = baseUrl;
  }

  async getExperiences(
    request: ExperienceRequest = {},
    accessToken?: string,
    refreshToken?: string
  ): Promise<ExperienceListResponse> {
    const url = `${this.baseUrl}/experiences/v1/list`;
    const headers = ExperienceTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 Sending Experience API Request:', {
        url,
        method: 'POST',
        requestFilters: request.filters,
        requestQueryParams: request.queryParams,
        hasPriceFilter: !!request.filters?.price,
        priceFilterValue: request.filters?.price,
        hasAuth: !!(accessToken && refreshToken),
        fullRequest: request,
      });
    }

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          list?: Experience[];
          pagination?: {
            totalItems?: number;
            currentPage?: number;
            itemsPerPage?: number;
            hasNextPage?: boolean;
          };
        };
      }>(url, request, { headers });

      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Experience API Response:', {
          success: response.data?.success,
          code: response.data?.code,
          experienceCount: response.data?.data?.list?.length || 0,
          pagination: response.data?.data?.pagination,
        });
      }

      return ExperienceTransformer.transformExperienceList(response.data);
    } catch (error) {
      this.handleError('getExperiences', error);
      throw error;
    }
  }

  async getFeaturedExperiences(
    accessToken?: string,
    refreshToken?: string
  ): Promise<Experience[]> {
    const request: ExperienceRequest = {
      queryParams: {
        page: 1,
        limit: 20,
        // Sort by rating to get best experiences
      },
      filters: {
        random: true,
      },
    };

    try {
      const response = await this.getExperiences(
        request,
        accessToken,
        refreshToken
      );
      return response.experiences || [];
    } catch (error) {
      this.logWarning('getFeaturedExperiences', accessToken, refreshToken);
      console.error('Error fetching featured experiences:', error);
      return [];
    }
  }

  async getSignatureJourneys(
    accessToken?: string,
    refreshToken?: string
  ): Promise<Experience[]> {
    const request: ExperienceRequest = {
      queryParams: {
        page: 1,
        limit: 20,
      },
      filters: {
        curated: true,
        type: 'package',
        random: true,
      },
    };

    try {
      const response = await this.getExperiences(
        request,
        accessToken,
        refreshToken
      );
      return response.experiences || [];
    } catch (error) {
      console.error('Error fetching signature journeys:', error);
      return [];
    }
  }

  async getCuratedPackages(
    accessToken?: string,
    refreshToken?: string
  ): Promise<Experience[]> {
    const request: ExperienceRequest = {
      queryParams: {
        page: 1,
        limit: 20, // Increased from 3 to fetch all curated packages
      },
      filters: {
        curated: false,
        type: 'package',
        random: true,
      },
    };

    try {
      const response = await this.getExperiences(
        request,
        accessToken,
        refreshToken
      );
      return response.experiences || [];
    } catch (error) {
      console.error('Error fetching curated packages:', error);
      return [];
    }
  }

  async getBoutiqueStays(
    accessToken?: string,
    refreshToken?: string
  ): Promise<Experience[]> {
    const request: ExperienceRequest = {
      queryParams: {
        page: 1,
        limit: 20,
      },
      filters: {
        type: 'stay_experience',
        random: true,
      },
    };

    try {
      const response = await this.getExperiences(
        request,
        accessToken,
        refreshToken
      );
      return response.experiences || [];
    } catch (error) {
      console.error('Error fetching boutique stays:', error);
      return [];
    }
  }

  async getGuidedActivities(
    accessToken?: string,
    refreshToken?: string
  ): Promise<Experience[]> {
    const request: ExperienceRequest = {
      queryParams: {
        page: 1,
        limit: 20,
        sort: { averageRating: -1 },
      },
      filters: {
        type: 'activity',
        random: true,
      },
    };

    try {
      const response = await this.getExperiences(
        request,
        accessToken,
        refreshToken
      );
      return response.experiences || [];
    } catch (error) {
      console.error('Error fetching guided activities:', error);
      return [];
    }
  }

  async getRidersCorner(
    accessToken?: string,
    refreshToken?: string
  ): Promise<Experience[]> {
    const request: ExperienceRequest = {
      queryParams: {
        page: 1,
        limit: 20,
        sort: { averageRating: -1 },
      },
      filters: {
        type: 'riders_corner',
        random: true,
      },
    };

    try {
      const response = await this.getExperiences(
        request,
        accessToken,
        refreshToken
      );
      return response.experiences || [];
    } catch (error) {
      console.error('Error fetching riders corner:', error);
      return [];
    }
  }

  async getExperienceStats(
    accessToken?: string,
    refreshToken?: string
  ): Promise<ExperienceStats> {
    const url = `${this.baseUrl}/experiences/v1/stats`;
    const headers = ExperienceTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.get<{
        code?: number;
        success?: boolean;
        data?: {
          signatureJourneys?: number;
          packages?: number;
          boutiqueStays?: number;
          guidedActivities?: number;
          ridersGuides?: number;
        };
      }>(url, { headers });

      return ExperienceTransformer.transformStats(response.data);
    } catch (error) {
      console.error('Error fetching experience stats:', error);
      // Return default stats if API fails
      return {
        signatureJourneys: 0,
        curatedPackages: 0,
        boutiqueStays: 0,
        guidedActivities: 0,
        ridersGuides: 0,
      };
    }
  }

  private handleError(operation: string, error: unknown): void {
    if (error instanceof Error) {
      const status = (error as AxiosError).response?.status;
      const statusText = (error as AxiosError).response?.statusText;
      const errorMessage = (error as AxiosError).response?.data as {
        message?: string;
      };

      let fullErrorMessage = `Failed to ${operation}: ${status} ${statusText}`;
      if (errorMessage) {
        fullErrorMessage += ` - ${errorMessage}`;
      }

      console.error('API Error:', {
        operation,
        status,
        statusText,
        message: errorMessage,
      });

      throw new Error(fullErrorMessage);
    }

    console.error(`Error in ${operation}:`, error);
    throw error;
  }

  private logWarning(
    operation: string,
    accessToken?: string,
    refreshToken?: string
  ): void {
    if (
      (!accessToken || !refreshToken) &&
      process.env.NODE_ENV === 'development'
    ) {
      console.warn(
        `${operation}: No auth tokens provided. API may require authentication.`
      );
    }
  }
}
